import { ChatFeatureIcon } from '@c/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { TabItem } from '@ui/Tabs';
import { getTwentyChats } from '@util/firestore/messages';
import { useTabs } from '@util/hooks/useTabs';
import { ChatDocument } from '@util/types/firestore/chat';
import { useAuth } from 'context/AuthContext';
import MessagePreview from './MessagePreview';
import useRealtimeChatsByChatIds from '@util/hooks/useRealtimeChatsByChatIds';
import { reportDashboardError } from '@util/reportDashboardError';
import QueryErrorState from './QueryErrorState';
import { useMemo } from 'react';

const Conversations = ({
  conversations,
  onConversationSelected,
  selectedChat,
}: {
  conversations: ChatDocument[];
  onConversationSelected: (chat: ChatDocument) => void;
  selectedChat: ChatDocument | undefined;
}) => {
  return (
    <>
      {!conversations.length && (
        <div className="flex h-full w-full flex-col items-center justify-center gap-[1.6rem] lg:max-h-[60rem]">
          <ChatFeatureIcon />
          <h3 className="text-[2.4rem] font-bold ">No Messages</h3>
          <p>Your messages will appear here</p>
        </div>
      )}
      {!!conversations.length &&
        conversations.map((conversation, index) => {
          if (conversation.id) {
            return (
              <MessagePreview
                chat={conversation}
                selected={selectedChat?.id === conversation.id}
                key={conversation.id + '-' + index}
                onClick={() => {
                  if (conversation.id) {
                    onConversationSelected(conversation);
                  }
                }}
              />
            );
          }
        })}
    </>
  );
};

type ConversationListProps = {
  onConversationSelected: (chat: ChatDocument) => void;
  conversations: ChatDocument[] | null;
  selectedChat: ChatDocument | undefined;
  variant: 'messages' | 'offers' | 'orderDetail';
};

export default function ConversationList({
  onConversationSelected,
  selectedChat,
  conversations,
  variant,
}: ConversationListProps) {
  const { userDoc } = useAuth();
  // The ALL tab only uses the platform-wide admin source on the messages page.
  const isAdminInbox = !!userDoc?.roles?.admin && variant === 'messages';
  const {
    data: adminChats,
    isLoading,
    isError: isAdminChatsError,
    refetch: refetchAdminChats,
    isFetching: isFetchingAdminChats,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['adminChats'],
    enabled: isAdminInbox,
    queryFn: ({ pageParam }) => getTwentyChats(pageParam),
    // This query is the ALL tab's only source of conversations. It had no
    // error handling at all, so a failed read left the support team looking
    // at an empty inbox with nothing recorded anywhere.
    onError: (err: unknown) =>
      reportDashboardError('dashboard/messages: getTwentyChats failed', err),
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 20) return undefined;
      return lastPage[lastPage.length - 1].last_time;
    },
  });

  //  Listen to the first 10 admin chats in real time
  const firstTenIds = useMemo(() => {
    // No placeholder id. The admin query above is disabled for ordinary
    // viewers, so this ran on every non-admin visit and sent a real Firestore
    // read for a chat that cannot exist.
    if (!adminChats?.pages) return undefined;
    return adminChats?.pages
      .flat()
      .slice(0, 10)
      .map((chat) => chat.id!);
  }, [adminChats?.pages]);
  const realtimeAdminChats = useRealtimeChatsByChatIds(firstTenIds);

  // The ALL tab used to render `realtime ?? []` followed by
  // `adminChats.slice(10)`, dropping the first ten fetched conversations on
  // the assumption that the realtime listener would supply them. That makes
  // the tab's contents depend on a listener having delivered, for rows we
  // already hold. Render everything fetched and let the snapshot UPDATE those
  // rows instead, newest first — the listener's own ordering used to provide
  // recency and the fetch order alone would not.
  const adminAllChats = useMemo(() => {
    const fetched = adminChats?.pages.flat() ?? [];
    const live = realtimeAdminChats.realtimeChats;
    const liveById = new Map((live ?? []).map((chat) => [chat.id, chat]));
    // Dedupe by id: pages come from a cursor-paginated read that can overlap.
    const byId = new Map<string | undefined, ChatDocument>();
    for (const chat of fetched) {
      if (!byId.has(chat.id)) byId.set(chat.id, liveById.get(chat.id) ?? chat);
    }
    return [...byId.values()].sort(
      (a, b) => (b.last_time ?? 0) - (a.last_time ?? 0)
    );
  }, [adminChats?.pages, realtimeAdminChats.realtimeChats]);

  // An admin whose chat list is still loading, or whose read failed, was shown
  // the "No Messages" empty state — the same "loading and failed look exactly
  // like empty" defect as GB #1462, on the one screen that fix did not reach.
  const adminAllLoading =
    isAdminInbox && isLoading && adminAllChats.length === 0;
  const adminAllFailed =
    isAdminInbox && isAdminChatsError && adminAllChats.length === 0;

  const sellerConverstaions = conversations?.filter(
    (conversation) =>
      conversation.seller_id === userDoc?.uid &&
      conversation.uids?.includes(userDoc?.uid)
  );

  const buyerConversations = conversations?.filter(
    (conversation) =>
      conversation.buyer_id === userDoc?.uid &&
      conversation.uids?.includes(userDoc?.uid)
  );

  function onScroll(ev: React.UIEvent<HTMLDivElement, UIEvent>) {
    if (isLoading || isFetchingNextPage || !hasNextPage) return;
    const el = ev.target as HTMLDivElement;
    // if scroll is at top of the page, refresh the chats
    if (el.scrollTop === 0) {
      refetch();
      return;
    }

    const threshold = el.clientHeight * 0.5;
    const thresholdReached =
      el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (thresholdReached) fetchNextPage();
  }

  const tabItems: TabItem[] = [
    {
      title: 'Buying',
      unreads: userDoc
        ? buyerConversations?.reduce(
            (acc, chat) => (chat.unread?.[userDoc.uid] ? acc + 1 : acc),
            0
          )
        : undefined,
      contents: (
        <div className="grow overflow-y-auto">
          {!!buyerConversations && (
            <Conversations
              conversations={buyerConversations}
              onConversationSelected={onConversationSelected}
              selectedChat={selectedChat}
            />
          )}
        </div>
      ),
    },
    {
      title: 'Selling',
      unreads: userDoc
        ? sellerConverstaions?.reduce(
            (acc, chat) => (chat.unread?.[userDoc.uid] ? acc + 1 : acc),
            0
          )
        : undefined,
      contents: (
        <div className="grow overflow-y-auto">
          {!!sellerConverstaions && (
            <Conversations
              conversations={sellerConverstaions}
              onConversationSelected={onConversationSelected}
              selectedChat={selectedChat}
            />
          )}
        </div>
      ),
    },
    {
      title: 'All',
      contents: (
        <div
          className="grow overflow-y-auto overscroll-y-contain"
          onScroll={onScroll}
        >
          {adminAllLoading ? (
            <div
              className="flex w-full flex-col gap-[1.6rem] p-[1.6rem]"
              aria-busy="true"
              aria-label="Loading conversations"
            >
              {new Array(5).fill(1).map((_a, i) => (
                <div key={i} className="flex w-full items-center gap-[1.6rem]">
                  <div className="h-[4.8rem] w-[4.8rem] shrink-0 animate-pulse rounded-full bg-brand-light-gray" />
                  <div className="flex w-full flex-col gap-[0.8rem]">
                    <div className="h-[1.6rem] w-1/3 animate-pulse rounded bg-brand-light-gray" />
                    <div className="h-[1.6rem] w-2/3 animate-pulse rounded bg-brand-light-gray" />
                  </div>
                </div>
              ))}
            </div>
          ) : adminAllFailed ? (
            <QueryErrorState
              subject="conversations"
              onRetry={() => refetchAdminChats()}
              isRetrying={isFetchingAdminChats}
            />
          ) : (
            !!conversations && (
              <Conversations
                conversations={
                  isAdminInbox ? adminAllChats : conversations ?? []
                }
                onConversationSelected={onConversationSelected}
                selectedChat={selectedChat}
              />
            )
          )}
        </div>
      ),
    },
  ];

  

  const { currentBody, tabs } = useTabs({ tabItems, full: true, initial: 2 });
  if (!userDoc) return null;

  return (
    <div className="flex h-full flex-col overflow-y-auto border-0 lg:border-r-[1px]">
      <div className="shrink">{tabs}</div>
      {currentBody}
    </div>
  );
}
